import { Stack } from "@mui/material";
import { useCensusUploadCheck } from "@platform/census-validation";
import { Alert, Button, Typography } from "@platform/design-system";

import { useRfpContext } from "../contexts";

import { useCensusCheckForBroker } from "./documents/useCensusCheckForBroker";
import { newestCensusDocumentId } from "./newestCensusDocumentId";

interface CensusCheckSummaryProps {
  readonly onReview: () => void;
}

const plural = (count: number, noun: string): string =>
  `${count} ${noun}${count === 1 ? "" : "s"}`;

const NewestCensusAlert = ({
  rfpId,
  documentId,
  onReview,
}: CensusCheckSummaryProps & {
  readonly rfpId: number;
  readonly documentId: number;
}): React.JSX.Element | null => {
  const state = useCensusUploadCheck(rfpId, documentId);

  if (state.kind !== "checked") {
    return null;
  }

  const { outcome, suggestions } = state.check;
  const isUnreadable = outcome !== "parsed";

  if (!isUnreadable && suggestions.length === 0) {
    return null;
  }

  return (
    <Alert
      severity="warning"
      variant="outlined"
      title={
        isUnreadable
          ? "We couldn't read your census"
          : `Your census has ${plural(suggestions.length, "suggestion")}`
      }
      data-testid="census-check-summary"
    >
      <Stack gap={1} alignItems="flex-start">
        <Typography variant="body-medium">
          {isUnreadable
            ? "Upload a copy we can open so carriers can quote from it."
            : "Fixing them lets carriers quote from your file without coming back to you."}{" "}
          You can still submit your RFP.
        </Typography>
        <Button variant="tertiary" size="sm" onClick={onReview}>
          Review in Documents
        </Button>
      </Stack>
    </Alert>
  );
};

/**
 * On the Summary step, what the census check found in the census file the RFP
 * is quoted on, when there is something for the broker to fix.
 */
export const CensusCheckSummary = ({
  onReview,
}: CensusCheckSummaryProps): React.JSX.Element | null => {
  const { rfp } = useRfpContext();
  const checksCensus = useCensusCheckForBroker();
  const documentId = newestCensusDocumentId(rfp.uploadedFiles ?? []);
  const rfpId = rfp.id;

  if (!checksCensus || documentId === undefined || typeof rfpId !== "number") {
    return null;
  }

  return (
    <NewestCensusAlert
      rfpId={rfpId}
      documentId={documentId}
      onReview={onReview}
    />
  );
};
